import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, resolveApiMediaUrl } from '../api/client.js';
import { useTenant } from '../context/TenantContext.jsx';

const emptyCreateForm = () => ({
  nome: '',
  codigo: '',
  imagem: '',
});

/**
 * @param {Record<string, unknown>} s
 */
function sectionToEditForm(s) {
  return {
    id: s.id,
    nome: String(s.nome ?? ''),
    codigo: s.codigo != null ? String(s.codigo) : '',
    imagem: s.imagem != null ? String(s.imagem) : '',
    product_count: Number(s.product_count) || 0,
  };
}

function IconPencil() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function SectionsPage() {
  const { tenantId } = useTenant();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyCreateForm());
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [createPhotoFile, setCreatePhotoFile] = useState(null);
  const [createPhotoKey, setCreatePhotoKey] = useState(0);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPreviewFailed, setEditPreviewFailed] = useState(false);
  const [error, setError] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const filteredItems = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => String(s.nome ?? '').toLowerCase().includes(q));
  }, [items, nameFilter]);

  /** @param {number} sectionId */
  async function uploadSectionImage(tenantId, sectionId, file) {
    const fd = new FormData();
    fd.append('imagem', file);
    await api(`/tenants/${tenantId}/sections/${sectionId}/imagem`, {
      method: 'POST',
      body: fd,
    });
  }

  useEffect(() => {
    setEditPreviewFailed(false);
  }, [editing?.id, editing?.imagem]);

  async function load() {
    if (!tenantId) return;
    const data = await api(`/tenants/${tenantId}/sections`);
    setItems(data.sections || []);
  }

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [tenantId]);

  async function add(e) {
    e.preventDefault();
    if (!tenantId) return;
    setError('');
    try {
      const img = form.imagem.trim();
      const data = await api(`/tenants/${tenantId}/sections`, {
        method: 'POST',
        body: JSON.stringify({
          nome: form.nome.trim(),
          codigo: form.codigo.trim() || null,
          imagem: createPhotoFile ? null : img || null,
        }),
      });
      const sectionId = data?.section?.id;
      if (createPhotoFile && sectionId) {
        await uploadSectionImage(tenantId, sectionId, createPhotoFile);
      }
      setForm(emptyCreateForm());
      setCreatePhotoFile(null);
      setCreatePhotoKey((k) => k + 1);
      await load();
    } catch (err) {
      setError(err?.message || 'Erro ao criar secção');
    }
  }

  async function saveEdit(e) {
    e.preventDefault();
    if (!tenantId || !editing) return;
    setError('');
    try {
      const sectionId = editing.id;
      if (!sectionId) {
        const data = await api(`/tenants/${tenantId}/sections`, {
          method: 'POST',
          body: JSON.stringify({
            nome: editing.nome.trim(),
            codigo: editing.codigo.trim() || null,
            imagem: editPhotoFile ? null : editing.imagem.trim() || null,
          }),
        });
        const newId = data?.section?.id;
        if (editPhotoFile && newId) {
          await uploadSectionImage(tenantId, newId, editPhotoFile);
        }
      } else {
        const body = {
          nome: editing.nome.trim(),
          codigo: editing.codigo.trim() || null,
        };
        if (!editPhotoFile) {
          body.imagem = editing.imagem.trim() || null;
        }
        await api(`/tenants/${tenantId}/sections/${sectionId}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        if (editPhotoFile) {
          await uploadSectionImage(tenantId, sectionId, editPhotoFile);
        }
      }
      setEditing(null);
      setEditPhotoFile(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Erro ao guardar secção');
    }
  }

  async function confirmDelete() {
    if (!tenantId || !deleteTarget?.id) return;
    setError('');
    try {
      await api(`/tenants/${tenantId}/sections/${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Erro ao remover cadastro da secção');
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Secções</h1>
          <p className="mt-1 text-sm text-ink/65 dark:text-mist/65">
            Cadastre categorias com foto — igual ao fluxo de produtos. Os produtos continuam a usar o nome da secção no
            campo «Seção».
          </p>
        </div>
        <Link
          to="/app/produtos"
          className="text-sm font-semibold text-accent underline-offset-4 hover:underline"
        >
          ← Produtos
        </Link>
      </div>

      <form
        onSubmit={add}
        className="grid gap-4 rounded-3xl border border-ink/10 bg-white/80 p-6 shadow-lg dark:border-white/10 dark:bg-white/5 md:grid-cols-2"
      >
        <p className="md:col-span-2 text-sm font-bold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
          Nova secção
        </p>
        <label className="text-sm font-semibold">
          Nome da secção
          <input
            required
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            placeholder="Ex.: Bebidas"
          />
        </label>
        <label className="text-sm font-semibold">
          Código ERP (opcional)
          <input
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
            value={form.codigo}
            onChange={(e) => setForm({ ...form, codigo: e.target.value })}
            placeholder="Ex.: BEBIDAS"
          />
        </label>
        <label className="text-sm font-semibold md:col-span-2">
          URL da foto (opcional se enviar ficheiro)
          <input
            type="text"
            inputMode="url"
            placeholder="https://…"
            disabled={!!createPhotoFile}
            className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-white/10 dark:bg-ink"
            value={form.imagem}
            onChange={(e) => setForm({ ...form, imagem: e.target.value })}
          />
        </label>
        <label className="text-sm font-semibold md:col-span-2">
          Ou ficheiro (JPEG, PNG, WebP ou GIF, até ~2&nbsp;MiB)
          <input
            key={createPhotoKey}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
            className="mt-1 block w-full text-xs text-ink/70 file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white dark:text-mist/70 dark:file:bg-accent"
            onChange={(e) => setCreatePhotoFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {error && !editing ? <p className="md:col-span-2 text-sm font-semibold text-accent2">{error}</p> : null}
        <button
          type="submit"
          className="md:col-span-2 rounded-2xl bg-accent py-3 text-sm font-semibold text-white"
        >
          Adicionar secção
        </button>
      </form>

      <div className="overflow-x-auto rounded-3xl border border-ink/10 bg-white/80 shadow-lg dark:border-white/10 dark:bg-white/5">
        <div className="border-b border-ink/10 p-4 dark:border-white/5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-ink/55 dark:text-frost-muted">
            Filtrar por nome
            <input
              type="search"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Digite o nome da secção"
              className="mt-1.5 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal dark:border-white/10 dark:bg-ink"
            />
          </label>
        </div>
        <table className="w-full min-w-[32rem] text-left text-sm">
          <thead className="border-b border-ink/10 text-xs uppercase tracking-wide text-ink/50 dark:border-white/5 dark:text-frost-muted">
            <tr>
              <th className="px-4 py-3 font-semibold">Foto</th>
              <th className="px-4 py-3 font-semibold">Secção</th>
              <th className="px-4 py-3 font-semibold">Produtos</th>
              <th className="px-4 py-3 font-semibold">Código</th>
              <th className="px-4 py-3 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((s) => {
              const img = s.imagem ? String(s.imagem) : '';
              const hasCadastro = s.id != null;
              return (
                <tr key={String(s.nome)} className="border-b border-ink/5 dark:border-white/5">
                  <td className="px-4 py-3">
                    {img ? (
                      <img
                        src={resolveApiMediaUrl(img)}
                        alt=""
                        className="h-12 w-12 rounded-xl border border-ink/10 object-cover dark:border-white/10"
                      />
                    ) : (
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-ink/15 text-[10px] text-ink/40 dark:border-white/15 dark:text-mist/40">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {String(s.nome)}
                    {!hasCadastro ? (
                      <span className="mt-0.5 block text-[11px] font-normal text-amber-700 dark:text-amber-300">
                        Só em produtos — edite para adicionar foto
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{Number(s.product_count) || 0}</td>
                  <td className="px-4 py-3 font-mono text-xs text-ink/70 dark:text-mist/70">
                    {s.codigo ? String(s.codigo) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded-lg border border-ink/10 p-2 text-ink hover:bg-ink/5 dark:border-white/10 dark:hover:bg-white/5"
                        title="Editar"
                        onClick={() => {
                          setEditing(sectionToEditForm(s));
                          setEditPhotoFile(null);
                          setError('');
                        }}
                      >
                        <IconPencil />
                      </button>
                      {hasCadastro ? (
                        <button
                          type="button"
                          className="rounded-lg border border-red-200 p-2 text-red-700 hover:bg-red-50 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/40"
                          title="Remover cadastro"
                          onClick={() => setDeleteTarget(s)}
                        >
                          <IconTrash />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink/50 dark:text-mist/50">
                  Nenhuma secção encontrada.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {editing ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
          <form
            onSubmit={saveEdit}
            className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-ink"
          >
            <h2 className="text-lg font-bold">Editar secção</h2>
            <p className="mt-1 text-xs text-ink/60 dark:text-mist/60">
              {editing.id
                ? 'Alterar o nome actualiza todos os produtos desta secção.'
                : 'Será criado o cadastro com foto — os produtos já usam este nome.'}
            </p>
            <div className="mt-4 space-y-3">
              <label className="block text-sm font-semibold">
                Nome
                <input
                  required
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                  value={editing.nome}
                  onChange={(e) => setEditing({ ...editing, nome: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold">
                Código ERP (opcional)
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                  value={editing.codigo}
                  onChange={(e) => setEditing({ ...editing, codigo: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold">
                URL da foto
                <input
                  type="text"
                  inputMode="url"
                  disabled={!!editPhotoFile}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-white/10 dark:bg-ink"
                  value={editing.imagem}
                  onChange={(e) => setEditing({ ...editing, imagem: e.target.value })}
                />
              </label>
              {!editPhotoFile && editing.imagem.trim() !== '' && !editPreviewFailed ? (
                <img
                  src={resolveApiMediaUrl(editing.imagem.trim())}
                  alt=""
                  className="max-h-32 rounded-xl border border-ink/10 object-contain dark:border-white/10"
                  onError={() => setEditPreviewFailed(true)}
                />
              ) : null}
              <label className="block text-sm font-semibold">
                Ou novo ficheiro
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                  className="mt-1 block w-full text-xs"
                  onChange={(e) => setEditPhotoFile(e.target.files?.[0] ?? null)}
                />
              </label>
              {editPhotoFile ? (
                <p className="text-xs text-ink/60 dark:text-mist/60">Nova imagem: {editPhotoFile.name}</p>
              ) : null}
            </div>
            {error && editing ? <p className="mt-3 text-sm font-semibold text-accent2">{error}</p> : null}
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-2xl border border-ink/15 py-2.5 text-sm font-semibold"
                onClick={() => {
                  setEditing(null);
                  setEditPhotoFile(null);
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="flex-1 rounded-2xl bg-accent py-2.5 text-sm font-semibold text-white">
                Guardar
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-ink">
            <h2 className="text-lg font-bold">Remover cadastro?</h2>
            <p className="mt-2 text-sm text-ink/70 dark:text-mist/70">
              Remove apenas o registo da secção «{String(deleteTarget.nome)}» (foto e código ERP). Os produtos mantêm o
              nome da secção.
            </p>
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-2xl border border-ink/15 py-2.5 text-sm font-semibold"
                onClick={() => setDeleteTarget(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="flex-1 rounded-2xl bg-red-600 py-2.5 text-sm font-semibold text-white"
                onClick={confirmDelete}
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
